"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { PlazaCard } from "@/features/plaza/components/PlazaCard";
import { plazaSearchParams } from "@/features/plaza/server/filters";
import type { PlazaAvatar, PlazaFilters, PlazaResult } from "@/features/plaza/types";

import { PlazaCastingActions, type PlazaCastingContext } from "./PlazaCastingActions";

type PlazaPageResponse = {
  success: boolean;
  list: PlazaAvatar[];
  pagination: { page: number; totalPages: number };
};

export function PlazaInfiniteGrid({ filters, initialResult, casting }: {
  casting?: PlazaCastingContext;
  filters: PlazaFilters;
  initialResult: PlazaResult;
}) {
  const [items, setItems] = useState(initialResult.items);
  const [page, setPage] = useState(initialResult.page);
  const [hasMore, setHasMore] = useState(initialResult.page < initialResult.pageCount);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => () => {
    requestRef.current?.abort();
    requestRef.current = null;
  }, []);

  const loadMore = useCallback(async () => {
    if (requestRef.current || !hasMore) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setFailed(false);
    try {
      const query = plazaSearchParams(filters, { page: page + 1 }).split("?")[1];
      const response = await fetch(`/api/v1/plaza/avatars?${query}`, { signal: controller.signal });
      if (!response.ok) throw new Error("Plaza request failed");
      const data: PlazaPageResponse = await response.json();
      if (!data.success || !Array.isArray(data.list) || !Number.isSafeInteger(data.pagination?.page)
        || !Number.isSafeInteger(data.pagination?.totalPages)) throw new Error("Invalid plaza response");
      if (controller.signal.aborted) return;
      // A refreshed catalog can shrink and clamp the requested page to its last page.
      if (data.pagination.page > page) {
        setItems(current => {
          const seen = new Set(current.map(item => item.id));
          return [...current, ...data.list.filter(item => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          })];
        });
        setPage(data.pagination.page);
      }
      setHasMore(data.list.length > 0 && data.pagination.page > page && data.pagination.page < data.pagination.totalPages);
    } catch {
      if (!controller.signal.aborted) setFailed(true);
    } finally {
      if (!controller.signal.aborted) {
        requestRef.current = null;
        setLoading(false);
      }
    }
  }, [filters, hasMore, page]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading || failed || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) void loadMore();
    }, { rootMargin: "400px 0px" });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, failed, loadMore]);

  return (
    <>
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4" aria-busy={loading}>
        {items.map(avatar => <PlazaCard key={avatar.id} avatar={avatar} showPrice={Boolean(casting)} detailQuery={casting?.project?.avatarId === avatar.id ? `?${new URLSearchParams({ project: casting.project.id, role: casting.project.roleId })}` : ""} actions={casting && <PlazaCastingActions avatarId={avatar.id} context={casting} />} />)}
      </div>
      <div ref={sentinelRef} className="mt-8 flex min-h-12 items-center justify-center gap-3 text-xs text-[var(--color-muted)]">
        <span role="status">{loading ? "加载中…" : failed ? "加载失败，请重试" : !hasMore ? "已全部加载" : ""}</span>
        {hasMore && !loading && <button type="button" onClick={() => void loadMore()} className="rounded-full border border-[var(--color-line)] bg-[var(--color-raised)] px-4 py-2 hover:text-[var(--color-paper)]">
          {failed ? "重试" : "加载更多"}
        </button>}
      </div>
    </>
  );
}
